import { AddTokenToWalletButton } from '@/components/add-token-to-wallet-button';
import { ConnectWalletButton } from '@/components/connect-wallet-button';
import { GoonAvatar } from '@/components/goon-avatar';
import { RoundedBadge } from '@/components/rounded-badge';
import { RoundedButton } from '@/components/rounded-button';
import { TokenIconList } from '@/components/token-icon-list';
import { TooltipIcon } from '@/components/tooltip-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { useNucleusRoycoPositionsData, useRoycoPositionDataForVaultKey } from '@/hooks/useRoycoPositionsData';
import { useVaultPortfolioData } from '@/hooks/useVaultPortfolioData';
import { formatListWithAnd, getIconPathFromTokenSymbol, getTimeUntilUnlock } from '@/utils';
import {
  Box,
  Button,
  Card,
  Flex,
  FormatNumber,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useAppKitAccount } from '@reown/appkit/react';
import { ComponentProps, useMemo } from 'react';
import { PiHourglassSimpleHighFill } from 'react-icons/pi';
import { useLocation, useNavigate, useParams } from 'react-router';
import { TOTAL_BALANCE_TOOLTIP_TEXT } from '../constants';
import { useCurrentVault } from './hooks/useCurrentVault';

export const Balance = ({ onClickHowItWorks }: { onClickHowItWorks: () => void }) => {
  const { vaultAddress } = useParams() as { vaultAddress: EthAddress };
  const { address } = useAppKitAccount();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const { pathname } = useLocation();

  //   TODO: clean this up, think about how to better organize endpoints so we dont need to hardcode magic strings
  const goToDeposits = () => {
    navigate(`${pathname}/deposit`);
  };
  const goToRoycoOnlyDeposits = () => {
    navigate(`${pathname}/deposit?royco_only=true`);
  };

  const goToVaultOnlyWithdrawals = () => {
    navigate(`${pathname}/withdraw`);
  };

  const { vault, isLoadingVault, chainId, vaultKey, iconPath, marketId } = useCurrentVault();
  const { decimals } = vault?.token ?? {};
  const depositTokens = Object.values(vault?.deposit.sourceChains[chainId]?.depositTokens ?? {});
  const {
    isLoadingRoycoPositionsData,
    totalPlumeClaimable,
    totalPlumeEarned,
    roycoPositionsData,
    countsTowardsBalance,
  } = useNucleusRoycoPositionsData();

  const { wPlumeEarningsUsd, currentVaultRoycoPositionData } = useRoycoPositionDataForVaultKey({ vaultKey }) ?? {};

  const oldestPosition = currentVaultRoycoPositionData?.at(0);

  const { vaultPortfolioData, isLoadingVaultPortfolioData } = useVaultPortfolioData();
  const currentVaultBalanceData = vaultPortfolioData?.vaults?.[vaultAddress.toLowerCase()];
  const vaultBalanceUSD = currentVaultBalanceData?.total_value_in_usd ?? 0;
  const vaultBalanceNative = currentVaultBalanceData?.total_value_in_base ?? 0;

  const totalBalanceUsd = useMemo(() => {
    const totalRoycoBalanceUsd =
      roycoPositionsData
        ?.filter((position) => position.marketId === marketId && countsTowardsBalance(position))
        .reduce((total, position) => total + position.balanceInUsd, 0) ?? 0;

    return totalRoycoBalanceUsd + vaultBalanceUSD + wPlumeEarningsUsd;
  }, [countsTowardsBalance, marketId, roycoPositionsData, vaultBalanceUSD, wPlumeEarningsUsd]);

  const totalRoycoBalanceNative =
    roycoPositionsData
      ?.filter((position) => position.marketId === marketId && countsTowardsBalance(position))
      .reduce((total, position) => total + position.balanceScaledDown, 0) ?? 0;
  if (!vaultAddress) return null;
  if (isLoadingVaultPortfolioData || isLoadingRoycoPositionsData || isLoadingVault) {
    return <BalanceSkeleton />;
  }
  if (totalBalanceUsd === 0) {
    return (
      <VStack textAlign="center" gap={6} h="full">
        <Spacer flex={1} />
        <Box boxSize={88} position="relative">
          {isAuthenticated ? (
            <GoonAvatar seed={address} boxSize={88} />
          ) : (
            <Image src="/assets/vaults/yield-optimizer.svg" boxSize={88} />
          )}
          <Box position="absolute" bottom={-2} right={-2} h={10} w={10} bgColor="bg" zIndex={2} borderRadius="xl" p={1}>
            <Image boxSize={8} src={iconPath} borderWidth="thick" borderColor="bg" />
          </Box>
        </Box>
        <Box spaceY={4}>
          <Heading size="2xl">Deposit to Earn</Heading>
          <Text fontSize="md" color="fg.muted" fontWeight="500">
            Start earning yield by depositing accepted assets
          </Text>
        </Box>
        <HStack>
          {isAuthenticated ? (
            <RoundedButton onClick={goToDeposits}>Deposit Funds</RoundedButton>
          ) : (
            <ConnectWalletButton />
          )}
          <RoundedButton variant="outline" onClick={onClickHowItWorks}>
            How It Works
          </RoundedButton>
        </HStack>
        <VStack alignItems="center">
          <TokenIconList tokens={depositTokens} loading={isLoadingVault} />
          <Text fontSize="md" color="fg.muted" fontWeight="500">
            Accepts {formatListWithAnd(depositTokens.map(({ symbol }) => symbol))}
          </Text>
        </VStack>
        <Spacer flex={1} />
      </VStack>
    );
  }
  return (
    <VStack gap={4} w="full" alignItems="stretch" h="full">
      <HStack justifyContent="space-between">
        <Flex alignItems="center">
          <Heading>Total Balance</Heading>
          <TooltipIcon ml={1} content={TOTAL_BALANCE_TOOLTIP_TEXT} />
        </Flex>
        <RoundedButton onClick={goToDeposits}>Deposit</RoundedButton>
      </HStack>
      <HStack>
        <Box>
          <Heading size="4xl" fontWeight="500">
            <Skeleton loading={isLoadingVaultPortfolioData || isLoadingRoycoPositionsData}>
              <FormatNumber
                value={totalBalanceUsd}
                maximumFractionDigits={2}
                minimumFractionDigits={2}
                style="currency"
                currency="USD"
              />
            </Skeleton>
          </Heading>
        </Box>
      </HStack>
      <Spacer flex={1} />
      <Position
        title="Vault Position"
        value={vaultBalanceNative}
        tokenSymbol={vaultKey}
        tokenAddress={vaultAddress}
        tokenDecimals={decimals}
        loading={isLoadingVault || isLoadingVaultPortfolioData}
        onClickWithdraw={goToVaultOnlyWithdrawals}
      />
      {totalRoycoBalanceNative ? (
        <HStack>
          <Text fontSize="sm" color="fg.muted" fontWeight="500">
            <FormatNumber value={totalRoycoBalanceNative} /> held in Royco
          </Text>
          <RoundedBadge fontSize="sm" color="fg.muted" fontWeight="500">
            <PiHourglassSimpleHighFill />{' '}
            {getTimeUntilUnlock(Number(oldestPosition?.unclaimedWPlumeIncentiveToken?.unlockTimestamp ?? 0))}
          </RoundedBadge>
        </HStack>
      ) : null}
      <HStack justifyContent="start">
        <AddTokenToWalletButton
          tokenSymbol={vaultKey}
          tokenAddress={vaultAddress}
          tokenDecimals={decimals}
          p={0.5}
          fontSize="sm"
          h="fit-content"
          color="fg.muted"
          loading={isLoadingVault}
        />
      </HStack>
      {marketId && vaultBalanceNative > 0 && (
        <DepositCtaToastButton onClick={goToRoycoOnlyDeposits} availableBalance={vaultBalanceNative} />
      )}
      <SimpleGrid gap={4} columns={2} mt={2}>
        <Card.Root borderRadius="2xl">
          <Card.Body p={4}>
            <Text fontSize="sm" color="fg.muted" fontWeight="500">
              Claimable $PLUME Incentives
            </Text>
            <Text fontSize="md" fontWeight="500" color="#00827B">
              <FormatNumber value={totalPlumeClaimable} maximumFractionDigits={2} /> PLUME
            </Text>
          </Card.Body>
        </Card.Root>
        <Card.Root borderRadius="2xl">
          <Card.Body p={4}>
            <Text fontSize="sm" color="fg.muted" fontWeight="500">
              Pending $PLUME Incentives
            </Text>
            <Text fontSize="md" fontWeight="500" color="#00827B">
              <FormatNumber value={totalPlumeEarned} maximumFractionDigits={2} /> PLUME
            </Text>
          </Card.Body>
        </Card.Root>
      </SimpleGrid>
    </VStack>
  );
};

export const Position = ({
  title,
  value,
  tokenSymbol,
  loading,
  onClickWithdraw,
  ...props
}: {
  title: string;
  value: number;
  tokenSymbol: string;
  tokenAddress: EthAddress;
  tokenDecimals: number | undefined;
  loading: boolean;
  onClickWithdraw: () => void;
} & ComponentProps<typeof Box>) => {
  return (
    <Box spaceY={2} {...props}>
      <Text fontSize="sm" fontWeight="500" color="fg.muted">
        {title}
      </Text>
      <HStack>
        <Skeleton loading={loading}>
          <Heading>
            <FormatNumber value={value} />
          </Heading>
        </Skeleton>
        <Image src={getIconPathFromTokenSymbol(tokenSymbol)} boxSize={6} />
        <Spacer flex={1} />
        {value ? (
          <RoundedButton variant="outline" onClick={onClickWithdraw}>
            Withdraw
          </RoundedButton>
        ) : null}
      </HStack>
    </Box>
  );
};

export const DepositCtaToastButton = ({
  onClick,
  availableBalance,
  ...props
}: {
  onClick: () => void;
  availableBalance: number;
} & ComponentProps<typeof Card.Root>) => {
  const { vaultKey } = useCurrentVault();
  return (
    <Card.Root
      as={Button}
      bgColor="#236B53"
      borderRadius="2xl"
      border="none"
      h={114}
      onClick={onClick}
      backgroundImage="url(/assets/vaults/cta-img.svg)"
      backgroundPosition="right"
      bgRepeat="no-repeat"
      {...props}
    >
      <Card.Body p={3}>
        <Stack h="full" justifyContent="center" flexDirection={['column', 'column', 'row']}>
          <Box spaceY={2}>
            <Text color="fg.inverted" fontWeight="600" whiteSpace="normal" wordBreak="break-word" w="full">
              Deposit {vaultKey} into Royco to earn PLUME incentives
            </Text>
            <Text color="fg.inverted" fontWeight="500" opacity="0.8">
              <FormatNumber value={availableBalance} /> {vaultKey} Available
            </Text>
          </Box>
          <Box>
            <RoundedBadge variant="surface">Deposit into Royco</RoundedBadge>
          </Box>
        </Stack>
      </Card.Body>
      {props.children}
    </Card.Root>
  );
};

const BalanceSkeleton = () => {
  return (
    <Box spaceY={6}>
      <HStack justifyContent="space-between">
        <Skeleton w="150px" h="32px" borderRadius="md" />
        <Skeleton w="80px" h="32px" borderRadius="md" />
      </HStack>

      <Skeleton w="240px" h="48px" borderRadius="md" mb={2} />

      <VStack gap={6} align="stretch">
        <SkeletonPositionCard />
        <SkeletonPositionCard />
        <Skeleton w="full" h="64px" borderRadius="2xl" />
      </VStack>
    </Box>
  );
};

const SkeletonPositionCard = () => {
  return (
    <Box spaceY={3}>
      <Skeleton w="120px" h="16px" />
      <HStack>
        <Skeleton w="100px" h="32px" />
        <Spacer flex={1} />
        <Skeleton w="80px" h="32px" borderRadius="md" />
      </HStack>
      <Skeleton w="160px" h="20px" />
    </Box>
  );
};
